import { useState } from 'react';

import { Badge, Button, EmptyState, type SurfaceTone } from '../surface.js';
import { Skeleton } from '../skeleton.js';

/**
 * The in-app inbox list — one component for every app that reads
 * `users/{uid}/inbox` (customer-pwa, agent-pwa). Presentation only: the host
 * owns the listener, the mark-read writes and every word of copy.
 *
 * States, all explicit (no-dead-ends): loading skeleton, error with retry,
 * empty that says WHAT will land here, and the list with an unread summary and
 * "Mark all as read".
 */

export interface InboxRow {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  /** Pre-formatted relative time ("5 min ago"). */
  readonly when: string;
  readonly unread: boolean;
  /** Optional kind chip ("Update", "Offer"). */
  readonly kindLabel?: { readonly text: string; readonly tone: SurfaceTone } | null;
}

export interface InboxListCopy {
  readonly errorTitle: string;
  readonly errorBody: string;
  readonly retry: string;
  readonly emptyTitle: string;
  readonly emptyBody: string;
  readonly markAllRead: string;
  readonly markingAllRead: string;
  /** Shown when the batched mark-read write fails, next to the still-available button. */
  readonly markAllFailed: string;
  /** e.g. "3 unread" — receives the count. */
  readonly unreadSummary: (count: number) => string;
  readonly allRead: string;
  readonly openAria: (title: string) => string;
}

export interface InboxListProps {
  readonly rows: readonly InboxRow[];
  readonly loading: boolean;
  readonly error: boolean;
  readonly onRetry: () => void;
  readonly onOpen: (id: string) => void;
  /** Resolve when the writes land; reject to keep the button available. */
  readonly onMarkAllRead: () => Promise<void>;
  readonly copy: InboxListCopy;
  /** Next step from the empty state (e.g. a "Back to home" link). */
  readonly emptyAction?: React.ReactNode;
  /**
   * Class for the message list, so a host app can lay it out at width (the
   * customer app turns it into a card grid on a tablet). The list's phone
   * styling stays inline here; the class only adds to it.
   */
  readonly listClassName?: string;
}

export function InboxList({
  rows,
  loading,
  error,
  onRetry,
  onOpen,
  onMarkAllRead,
  copy,
  emptyAction,
  listClassName,
}: InboxListProps) {
  const [marking, setMarking] = useState(false);
  const [markFailed, setMarkFailed] = useState(false);

  if (loading) {
    return (
      <div aria-hidden style={{ display: 'grid', gap: 'var(--space-2)' }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} height={76} radius="var(--radius-md)" style={{ display: 'block' }} />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert">
        <EmptyState
          title={copy.errorTitle}
          body={copy.errorBody}
          action={
            <Button variant="primary" onClick={onRetry}>
              {copy.retry}
            </Button>
          }
        />
      </div>
    );
  }
  if (rows.length === 0) {
    return <EmptyState title={copy.emptyTitle} body={copy.emptyBody} action={emptyAction} />;
  }

  const unread = rows.filter((r) => r.unread).length;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <span
          role="status"
          style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
        >
          {unread > 0 ? copy.unreadSummary(unread) : copy.allRead}
        </span>
        {unread > 0 ? (
          <Button
            variant="secondary"
            disabled={marking}
            onClick={() => {
              setMarking(true);
              setMarkFailed(false);
              void onMarkAllRead()
                .catch(() => setMarkFailed(true))
                .finally(() => setMarking(false));
            }}
          >
            {marking ? copy.markingAllRead : copy.markAllRead}
          </Button>
        ) : null}
      </div>
      {markFailed ? (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-danger-bg)',
            fontSize: 'var(--text-meta)',
          }}
        >
          {copy.markAllFailed}
        </p>
      ) : null}
      <ul
        className={listClassName}
        style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}
      >
        {rows.map((r) => (
          <li key={r.id}>
            <InboxRowButton
              row={r}
              ariaLabel={copy.openAria(r.title)}
              onOpen={() => onOpen(r.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function InboxRowButton({
  row,
  ariaLabel,
  onOpen,
}: {
  readonly row: InboxRow;
  readonly ariaLabel: string;
  readonly onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={ariaLabel}
      className="k-press"
      style={{
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        background: row.unread ? 'var(--color-primary-soft)' : 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-1)',
        padding: 'var(--space-4)',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 'var(--space-3)',
        alignItems: 'start',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 'var(--space-2)',
          height: 'var(--space-2)',
          marginTop: 'var(--space-2)',
          borderRadius: 'var(--radius-pill)',
          background: row.unread ? 'var(--color-primary)' : 'transparent',
        }}
      />
      <span style={{ minWidth: 0, display: 'grid', gap: 'var(--space-1)' }}>
        {row.kindLabel ? (
          <span>
            <Badge tone={row.kindLabel.tone}>{row.kindLabel.text}</Badge>
          </span>
        ) : null}
        <span style={{ fontWeight: row.unread ? 600 : 500, overflowWrap: 'anywhere' }}>
          {row.title}
        </span>
        <span
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
            overflowWrap: 'anywhere',
          }}
        >
          {row.body}
        </span>
      </span>
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          whiteSpace: 'nowrap',
        }}
      >
        {row.when}
      </span>
    </button>
  );
}

/**
 * The viewer-locale title/body of an inbox message. The server writes `title`
 * and `body` already resolved to the recipient's locale, and — newer messages —
 * `titleI18n`/`bodyI18n` maps so a reader can follow the viewer's CURRENT
 * language. Prefer the map entry, then the resolved string.
 */
export function localizedInboxText(
  m: {
    readonly title: string;
    readonly body: string;
    readonly titleI18n?: Readonly<Record<string, string>> | undefined;
    readonly bodyI18n?: Readonly<Record<string, string>> | undefined;
  },
  locale: string,
): { readonly title: string; readonly body: string } {
  const pick = (map: Readonly<Record<string, string>> | undefined, fallback: string) => {
    const v = map?.[locale];
    return typeof v === 'string' && v.trim() !== '' ? v : fallback;
  };
  return { title: pick(m.titleI18n, m.title), body: pick(m.bodyI18n, m.body) };
}
